import routes, { getBaseTitle, getRouteById, getSeoDataFromViewId } from 'defs/routes';
import i18n from 'helper/i18n';
import { template } from 'helper/string';
import { delayed } from 'lib/Job';
import * as React from 'react';
import { memo } from 'react';
import { Helmet } from 'react-helmet';
import { withRouter } from 'react-router';
import { WithRouterType } from 'types/withRouter';

import ProfileNotice from 'components/organisms/Profile/Notice';
import ConnectionRenderer from 'containers/helper/Connection/Renderer';
import ProfileFavorite from 'containers/organisms/Profile/Favorite/ProfileFavorite';
import ProfileSettings from 'containers/organisms/Profile/Settings/ProfileSettings';
import ProfileSlogan from 'containers/organisms/Profile/Slogan/ProfileSlogan';

import Coach from 'components/atoms/Coach';
import Link from 'components/atoms/Link';
import { LinkProps as InternalLinkProps } from 'components/atoms/Link';
import Bevel from 'components/helper/Bevel';
import { CoachIsEnumType } from 'components/molecules/CoachSelector';
import Summary from 'components/molecules/Summary';
import { SummaryProps } from 'components/molecules/Summary';
import { AllergyType, NutritionPhilosophyType } from 'types/DietaryHabits';
import {
	allergyList,
	mapAllergiesToIcon,
	mapNutritionPhilosophyToIcon,
	nutritionPhilosophyList,
} from '../../../defs/dietaryHabits';

export type LinkProps = InternalLinkProps & {
	modifier?: string;
	openInNewWindow?: boolean;
	iconLeft?: string;
	title: string;
	href?: string;
	externalHref?: string;
	iconRight?: string;
	onClick?: Function;
};

export type ProfileState = {
	settingUpdated: boolean;
};

export type ProfileProps = {
	currentViewID: string;
	intensity?: string;
	healthGoal?: string;
	fullName: string;
	genderMeta: Object;
	ready?: boolean;
	coach: CoachIsEnumType;
	height?: string;
	weight?: string;
	nutritionPhilosophy?: NutritionPhilosophyType;
	allergies?: AllergyType[];
	gender?: string;
	email?: string;
	firstName?: string;
	lastName?: string;
	birthdate?: string;
	slogan?: string;
	links: Array<LinkProps>;
	doAddEvents: Function;
} & WithRouterType;

const profileLabelBase = 'react.impulsCoach.views.Profile';

export const LINKABLE_ANAMNESE_ID = {
	selectDietaryHabits: 'selectDietaryHabits',
	selectActivityLevel: 'selectActivityLevel',
	dsgvo: 'dsgvo',
};
export type TLinkableAnamneseId = keyof typeof LINKABLE_ANAMNESE_ID;

const label = (subkey: string): string => {
	return i18n(`${profileLabelBase}.${subkey}`);
};

export class Profile extends React.Component<ProfileProps, ProfileState> {
	get profileBodySummary(): SummaryProps {
		return {
			title: label('bodyData'),
			elements: [
				{
					label: label('bodyData.height'),
					value: this.props.height ? this.props.height.toString() : label('defaultValue'),
					icon: 'measuring-tape',
				},
				{
					label: label('bodyData.weight'),
					value: this.props.weight ? this.props.weight.toString() : label('defaultValue'),
					icon: 'scale',
				},
			],
		};
	}

	get profileGoalSummary(): SummaryProps {
		return {
			title: label('targets'),
			elements: [
				{
					label: label('targets.healthTarget'),
					value: this.props.healthGoal,
					icon: 'wishlist',
				},
				{
					label: label('targets.intensity'),
					value: this.props.intensity,
					icon: 'clock',
					_target: {
						id: LINKABLE_ANAMNESE_ID.selectActivityLevel,
					},
				},
			],
		};
	}

	get profileDietaryHabitsSummary(): SummaryProps {
		return {
			title: label('dietaryHabits'),
			elements: [
				{
					label: label('bodyData.nutritionPhilosophy'),
					value: ((nutrition) => {
						if (nutrition) {
							const nutritionItem = nutritionPhilosophyList.find((el) => el.id === nutrition);
							return nutritionItem ? `${nutritionItem.text}` : label('defaultValue');
						}
						return label('defaultValue');
					})(this.props.nutritionPhilosophy),
					icon: ((nutrition) => {
						if (nutrition) {
							return mapNutritionPhilosophyToIcon(nutrition);
						}

						return label('defaultValue');
					})(this.props.nutritionPhilosophy),
					_target: {
						id: LINKABLE_ANAMNESE_ID.selectDietaryHabits,
					},
				},
				{
					label: label('bodyData.allergies'),
					value: ((allergies) => {
						if (allergies) {
							const allergiesItems = allergyList.filter((el) => allergies.includes(el.id));
							return allergiesItems.length !== 0
								? `${allergiesItems.map((i) => i.text).join(', ')}`
								: label('defaultValue');
						}

						return label('defaultValue');
					})(this.props.allergies),
					icon: ((allergies) => {
						if (allergies) {
							return mapAllergiesToIcon(allergies);
						}

						return label('defaultValue');
					})(this.props.allergies),
					_target: {
						id: LINKABLE_ANAMNESE_ID.selectDietaryHabits,
					},
				},
			],
		};
	}

	get profileDataSummary(): SummaryProps {
		return {
			title: label('personal'),
			elements: [
				{
					label: label('personal.email'),
					value: this.props.email,
					icon: 'email',
				},
				{
					label: label('personal.firstname'),
					value: this.props.firstName,
					icon: 'user-name',
				},
				{
					label: label('personal.lastname'),
					value: this.props.lastName,
					icon: 'user-prename',
				},
				{
					label: label('personal.birthdate'),
					value: this.props.birthdate,
					icon: 'cupcake',
				},
			],
		};
	}

	get seoData(): ReturnType<typeof getSeoDataFromViewId> {
		const { currentViewID } = this.props;
		return getSeoDataFromViewId(currentViewID);
	}

	public static displayName = 'Profile';
	public static defaultProps = {
		genderMeta: {},
		fullName: '',
		coach: '',
		links: [],
	};
	public state = {
		settingUpdated: false,
	};

	public handleSettingUpdated = () => {
		this.setState(
			{
				settingUpdated: true,
			},
			() => {
				delayed(() => {
					this.setState({ settingUpdated: false });
				}, 3000);
			}
		);
	};

	public render() {
		const { settingUpdated } = this.state;
		const { fullName, coach, links } = this.props;
		const profileLinks: LinkProps[] = links.map(
			(link: Object): LinkProps => ({
				...(link as LinkProps),
				modifier: 'base',
			})
		);
		const seoData = this.seoData;
		const compiledSeoTitle = template(seoData.title, {
			userName: fullName,
		});

		return (
			<div className="o-profile">
				<Helmet>
					<title>{`${compiledSeoTitle} ${getBaseTitle()}`}</title>
					<meta property="og:title" content={`${compiledSeoTitle} ${getBaseTitle()}`} />
				</Helmet>
				<ProfileNotice
					visible={settingUpdated}
					text={i18n('react.impulsCoach.views.Profile.notifications.settingChanged')}
				/>
				<div className="o-profile__header">
					<div className="o-profile__bg">
						<div className="visible-xs visible-ms">
							<Bevel modifier="bottom-left" color="00838f" />
						</div>
						<div className="hidden-xs hidden-ms">
							<Bevel modifier="bottom-left" color="ffffff" />
						</div>
					</div>
					<div className="container">
						<div className="row">
							<div className="col-xs-12">
								{fullName ? <h1 className="o-profile__header-user">{fullName}</h1> : null}
								<ProfileSlogan />
							</div>
						</div>
					</div>
				</div>
				<div className="container container--full-width-ms">
					<div className="row">
						<div className="col-xs-12">
							<div className="o-profile__content o-profile__content--green">
								<Summary {...this.profileBodySummary} />
								<Summary
									{...this.profileGoalSummary}
									onEdit={this.handleClickOnEditableProfileSetting}
								/>
								<Summary
									{...this.profileDietaryHabitsSummary}
									onEdit={this.handleClickOnEditableProfileSetting}
								/>
								<div className="o-profile__button">
									<Link
										modifier="button"
										title={label('targets.ctaText')}
										href={routes.anamnesis.selectCoach.href}
										openInNewWindow={false}
									/>
								</div>
							</div>
							<div className="o-profile__content">
								<Summary {...this.profileDataSummary} modifier="invert" />
								<div className="o-profile__button">
									<p className="o-profile__button-text">{label('personal.ctaDescription')}</p>
									<Link
										modifier="button-green"
										openInNewWindow
										title={label('personal.ctaText')}
										externalHref={label('personal.ctaLink')}
									/>
								</div>
							</div>
						</div>
					</div>
					<ConnectionRenderer watch="online">
						<ProfileFavorite />
					</ConnectionRenderer>
					<div className="o-profile__content">
						<div className="row">
							<div className="col-xs-12 col-md-7">
								{coach ? <h4 className="o-profile__title">{label('coach')}</h4> : null}
								{coach ? (
									<div className="o-profile__coach">
										<div className="o-profile__coach-cell">
											<div className="o-profile__coach-box">
												<div className="o-profile__coach-wrap">
													<Coach coach={coach} gesture="front-perfect" alt={coach} />
												</div>
											</div>
										</div>
										<div className="o-profile__coach-cell">
											<span className="o-profile__coach-title">
												{i18n(`react.impulsCoach.components.coachSelector.${coach}.name`)}
											</span>
											<p className="o-profile__coach-slogan">
												{i18n(`react.impulsCoach.views.Profile.coach.${coach}.slogan`)}
											</p>
										</div>
									</div>
								) : null}
								<ProfileSettings onSettingChanged={this.handleSettingUpdated} />
								{links && (
									<ul className="o-profile__list">
										<li className="o-profile__list-item">
											<Link
												modifier="base"
												title={i18n('react.impulsCoach.views.Profile.dsgvo.label')}
												onClick={() => {
													this.handleClickOnEditableProfileSetting('dsgvo');
												}}
												iconRight=""
											/>
										</li>
										{profileLinks.map((link: LinkProps, index: number) => {
											return (
												<li key={index} className="o-profile__list-item">
													<Link
														modifier={link.modifier}
														title={link.title}
														externalHref={link.href}
														openInNewWindow={link.openInNewWindow}
														iconRight={''}
													/>
												</li>
											);
										})}
									</ul>
								)}
								<p className="m-rich">{i18n('react.impulsCoach.views.Profile.linkText')}</p>
							</div>
						</div>
					</div>
				</div>
			</div>
		);
	}

	private handleClickOnEditableProfileSetting = (id: TLinkableAnamneseId) => {
		const profileRoute = getRouteById(this.props.currentViewID);
		if (!profileRoute) {
			return;
		}
		this.props.history.push(`${routes.anamnesis[id].href}?referrer=${profileRoute.href}`);
	};
}

export default withRouter(memo(Profile as any) as any);
